import * as PIXI from "pixi.js";
import { DropShadowFilter } from "pixi-filters";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";
import * as entitySvc from "@/services/entitySvc";
import * as utils from "@/utils";
import * as convUtils from "@/utils/convUtils";
import * as spriteURLs from "@/utils/pixiGrapher/spriteURLs";

gsap.registerPlugin(PixiPlugin);
PixiPlugin.registerPIXI(PIXI);

let matColDict = {
  "wood": "0x7f5347",
  "metal": "0x808080",
  "plastic": "0x0086c0",
  "cardboard": "0x8A7862",
  "glass": "0xa25ddc",
  "electronic": "0x588095",
  "biogas": "0x00a359",
  "steel": "0x6e6f8f",
  "vinyl": "0x00d1d1",
  "leather": "0x9d8f3e",
  "rubber": "0x0060b9",
  "compostable": "0x037f4c",
  "planks": "0xad3f51,"
};

export function getColForMat(matLbl) {
  let retCol = matColDict[matLbl];
  if (!retCol) retCol = "0x323338";
  return retCol;
}

let iconScaleFactor = 0.1;
let textScaleFactor = 0.3;

let itemNodeQtyStyle = {
  fill: "#323338",
  align: "right",
  fontFamily: "Roboto",
  fontWeight: "600",
  fontSize: "60px"
};

let itemNodeUtStyle = {
  fill: "#323338",
  align: "left",
  fontFamily: "Roboto",
  fontWeight: "600",
  fontSize: "40px"
};

let itemNodeLabelStyle = {
  fill: "#323338",
  align: "center",
  fontFamily: "Roboto",
  fontSize: "70px"
};

let materialNodeLabelStyle = {
  fill: "#323338",
  align: "center",
  fontFamily: "Roboto",
  fontWeight: "600",
  fontSize: "60px"
};

let convToLabelStyle = {
  fill: "#676879",
  align: "left",
  fontFamily: "Roboto",
  fontSize: "60px"
};


let blueRingCanvasSize = 2000;
let blueRingCanvas = document.createElement("canvas");
blueRingCanvas.width  = blueRingCanvasSize;
blueRingCanvas.height = blueRingCanvasSize;

let blueRingCtx = blueRingCanvas.getContext("2d");
blueRingCtx.strokeStyle = "#0073ea";
blueRingCtx.lineWidth = 2;
blueRingCtx.beginPath();
blueRingCtx.arc(blueRingCanvasSize/2, blueRingCanvasSize/2, 18, 0, 2 * Math.PI, false);
blueRingCtx.stroke();

let blueRingTexture = PIXI.Texture.from(blueRingCanvas);

// look at the incoming item and return most appropriate sprite from spriteOpts
// based on tags which overlap between the 2
export function getAppropriateSprite(item) {
  // start collecting unique tags (all lowercased)
  let itemTagsBuffer = new Set();
  let cleanedUpItemForSprGen = { ...entitySvc.getTemplateEntity(), ...item, };
  // split word by whitespace, comma, hyphens, underscores etc.
  cleanedUpItemForSprGen.name.split(/[\s.\*+-/_]/).forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  // add converts_to, converts_from, ingredients and tags vals as tags
  cleanedUpItemForSprGen.converts_to.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.converts_from.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.ingredients.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.tags.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  // add poss_converts_to, poss_converts_from, poss_ingredients and poss_tags vals as tags
  cleanedUpItemForSprGen.poss_converts_to.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.poss_converts_from.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.poss_ingredients.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  cleanedUpItemForSprGen.poss_tags.forEach(i => itemTagsBuffer.add(i.toLowerCase()));
  // add singular version by removing last 's' if word ends with 's' 
  itemTagsBuffer.forEach(it => {
    if (it.match(/^.*s$/)) {
      itemTagsBuffer.add(it.replace(/.$/, ''));
    }
  });
  // remove any charcter thats not alphabet; retain only words longer than 2 chars
  let itemTags = Array.from(itemTagsBuffer).map(t => t.replace(/[\W_]+/g, "")).filter(t => !!t && t.length > 2);
  // assigns points by counting overlaps between item-tags and sprite tags
  let sprtPts = spriteURLs.spriteOpts.map(sprInfo => {
    let overLappingTags = sprInfo.tags.filter(t => itemTags.includes(t));
    let pts = overLappingTags.length;
    let ptsObj = {
      sprInfo,
      pts,
    };
    return ptsObj;
  });
  // get the tag with the most tag overlaps matches
  sprtPts = sprtPts.sort((a, b) => a.pts > b.pts ? -1 : 1);
  // return a backup img if overlap is not too good.
  let bestMatch = sprtPts[0];
  let retSprUrl = bestMatch.sprInfo.src;
  // bestMatch.pts = 0;
  if (bestMatch.pts < 1) {
    retSprUrl = spriteURLs.unknownSprites[0].src;
  }
  return retSprUrl;
}

function getGfx_board_item(item) {

  let sprCont = new PIXI.Container();

  let isSelected = false;

  sprCont.myGfx = {
    setSelectionState: (newState) => {
      isSelected = newState;
      if (isSelected) {
        gsap.to(blueRingSelected, { pixi: { alpha: 1 }, duration: 0.1 }); // no-ease
        gsap.to(blueRingSelected, { pixi: { scale: 1.8 }, duration: 0.2, ease: "power1.out" });
      } else {
        gsap.to(blueRingSelected, { pixi: { scale: 2 }, duration: 0.2, ease: "power1.out" });
        gsap.to(blueRingSelected, { pixi: { alpha: 0 }, duration: 0.1 }); // no-ease
      }
    }
  };
  
  let shadow = new DropShadowFilter();
  shadow.blur = 12;
  shadow.alpha = 0.1;
  shadow.distance = 4;

  let bgCirc  = new PIXI.Graphics();
  bgCirc.beginFill(0xffffff);
  bgCirc.drawCircle(0, 0, 30);
  bgCirc.endFill();
  bgCirc.filters = [shadow];
  sprCont.addChild(bgCirc);

  let blueRing = new PIXI.Sprite(blueRingTexture);
  blueRing.anchor.set(0.5, 0.5);
  blueRing.scale.x = 0.5;
  blueRing.scale.y = 0.5;
  blueRing.alpha = 0;
  sprCont.addChild(blueRing);

  let blueRingSelected = new PIXI.Sprite(blueRingTexture);
  blueRingSelected.anchor.set(0.5, 0.5);
  blueRingSelected.scale.x = 2;
  blueRingSelected.scale.y = 2;
  blueRingSelected.alpha = 0;
  sprCont.addChild(blueRingSelected);

  let iconSprURL = getAppropriateSprite(item);
  let iconSpr = PIXI.Sprite.from(iconSprURL);
  iconSpr.anchor.set(0.5);
  iconSpr.height = 360;
  iconSpr.width = 360;
  let iconSprCont = new PIXI.Container();
  iconSprCont.addChild(iconSpr);
  iconSprCont.scale.x = iconScaleFactor;
  iconSprCont.scale.y = iconScaleFactor;
  sprCont.addChild(iconSprCont);

  let itemName = item.name || item.id;
  let lableStyleToUse = JSON.parse(JSON.stringify(itemNodeLabelStyle));
  // console.log(lableStyleToUse);
  let titleLblTxt = new PIXI.Text(itemName, lableStyleToUse);
  titleLblTxt.anchor.set(0.5, -1.7);
  titleLblTxt.scale.x = textScaleFactor;
  titleLblTxt.scale.y = textScaleFactor;
  sprCont.addChild(titleLblTxt);

  let convToLstCont = new PIXI.Container();
  for (let [i, c] of item.converts_to.entries()) {
    let convToIconCirc  = new PIXI.Graphics();
    let iconCol = getColForMat(c);
    convToIconCirc.beginFill(iconCol);
    convToIconCirc.drawCircle(0, 40, 16);
    convToIconCirc.endFill();
    convToIconCirc.position.set(0, i * 80);
    convToLstCont.addChild(convToIconCirc);

    let convToLblTxt = new PIXI.Text(c, convToLabelStyle);
    convToLblTxt.position.set(50, i * 80);
    convToLstCont.addChild(convToLblTxt);
  }
  convToLstCont.scale.x = textScaleFactor;
  convToLstCont.scale.y = textScaleFactor;
  convToLstCont.position.set(54, -30);
  sprCont.addChild(convToLstCont);

  let qtyLblCont = new PIXI.Container();

  let qty = (item
    .column_values
    .find(cv => 
      convUtils.performFuzzyTextMatch(
        cv.title,
        [
          "quantity",
          "amount",
          "count",
          "volume"
        ],
        { similarityThreshold: 0.8 }
      )) || {}).text;
  
  if (!!qty) {
    let lableStyleForQty = JSON.parse(JSON.stringify(itemNodeQtyStyle));
    let qtyLblTxt = new PIXI.Text(qty, lableStyleForQty);
    qtyLblTxt.anchor.set(1, 3);
    qtyLblTxt.scale.x = textScaleFactor;
    qtyLblTxt.scale.y = textScaleFactor;
    qtyLblCont.addChild(qtyLblTxt);

    let unit = (item
      .column_values
      .find(cv =>
        convUtils.performFuzzyTextMatch(
          cv.title,
          [
            "units",
            "rate",
            "measure"
          ],
          { similarityThreshold: 0.8 }
        )) || {}).text;
    if (!unit) unit = "unit";

    let lableStyleForUts = JSON.parse(JSON.stringify(itemNodeUtStyle));
    let utsLblTxt = new PIXI.Text(unit, lableStyleForUts);
    utsLblTxt.anchor.set(-0.1, 4);
    utsLblTxt.scale.x = textScaleFactor;
    utsLblTxt.scale.y = textScaleFactor;
    qtyLblCont.addChild(utsLblTxt);

    sprCont.addChild(qtyLblCont);
  }

  sprCont.scale.set(1.2);
  gsap.to(sprCont, { pixi: { scale: 1 }, duration: 0.2, ease: "power3.in" });
  gsap.to(convToLstCont, { pixi: { positionX: 50 }, delay: 0.4, duration: 0.2, ease: "power3.out" });
  gsap.to(convToLstCont, { pixi: { alpha: 0 }, duration: 1.2, delay: 0.3 }); // no ease
  gsap.to(qtyLblCont, { pixi: { alpha: 0 }, duration: 1.2, delay: 0.3 }); // no ease

  sprCont.interactive = true;
  sprCont.buttonMode = true;
  sprCont.hitArea = new PIXI.Circle(0, 0, 60);

  function onPointerIn(evt) {
    iconSpr.tint = 0x999999;
    gsap.to(bgCirc, {pixi: { scale: 0.6 }, duration: 0.3, ease: "power3.in" });
    gsap.to(bgCirc, { pixi: { tint: 0x999999 }, duration: 0.2 }); // no ease
    gsap.to(titleLblTxt, { pixi: { positionY: -6 }, duration: 0.2 }); // no ease
    gsap.to(titleLblTxt, { pixi: { scale: textScaleFactor * 1.05 }, duration: 0.2 }); // no ease
    gsap.to(convToLstCont, { pixi: { positionX: 54 }, duration: 0.2, ease: "power3.out" });
    gsap.to(convToLstCont, { pixi: { alpha: 1 }, duration: 0.2 }); // no ease
    gsap.to(iconSprCont, { pixi: { scale: iconScaleFactor * 1.6 }, duration: 0.2, delay: 0.1, ease: "power1.in" });
    gsap.to(qtyLblCont, { pixi: { positionY: 4 }, duration: 0.2 }); // no ease
    gsap.to(qtyLblCont, { pixi: { alpha: 1 }, duration: 0.2 }); // no ease
    gsap.to(blueRing, { pixi: { alpha: 0.4 }, duration: 0.2 }); // no ease
    gsap.to(blueRing, { pixi: { scale: 2 }, duration: 0.2, ease: "power1.out" });
  }
  
  function onPointerOut(evt) {
    iconSpr.tint = 0xFFFFFF;
    gsap.to(bgCirc, {pixi: { scale: 1 }, duration: 0.3, ease: "power3.in" });
    gsap.to(bgCirc, { pixi: { tint: 0xFFFFFF }, duration: 0.2 }); // no ease
    gsap.to(titleLblTxt, { pixi: { positionY: 0 }, duration: 0.2 }); // no ease
    gsap.to(titleLblTxt, { pixi: { scale: textScaleFactor }, duration: 0.2 }); // no ease
    gsap.to(convToLstCont, { pixi: { positionX: 50 }, duration: 0.2, ease: "power3.out" });
    gsap.to(convToLstCont, { pixi: { alpha: 0 }, duration: 0.2 }); // no ease
    gsap.to(iconSprCont, { pixi: { scale: iconScaleFactor }, duration: 0.2, delay: 0.1, ease: "power1.in" });
    gsap.to(qtyLblCont, { pixi: { positionY: 0 }, duration: 0.2 }); // no ease
    gsap.to(qtyLblCont, { pixi: { alpha: 0 }, duration: 0.2 }); // no ease
    gsap.to(blueRing, { pixi: { alpha: 0 }, duration: 0.6 }); // no ease
    gsap.to(blueRing, { pixi: { scale: 0.5 }, duration: 0.6, ease: "power1.out" });
  }

  function onPointerDown(evt) {
    iconSpr.tint = 0x888888;
  }

  function onPointerUp(evt) {
    iconSpr.tint = 0x999999;
  }

  sprCont.on("pointerover", onPointerIn);
  sprCont.on("pointerout", onPointerOut);
  sprCont.on("pointerdown", onPointerDown);
  sprCont.on("pointerup", onPointerUp);

  return sprCont;
}

function getGfx_material_node(item) {
  let sprCont = new PIXI.Container();
  
  let shadow = new DropShadowFilter();
  shadow.blur = 8;
  shadow.alpha = 0.2;
  shadow.distance = 2;

  let bgCirc  = new PIXI.Graphics();
  bgCirc.beginFill(0xffffff);
  bgCirc.drawCircle(0, 0, 8);
  bgCirc.endFill();
  bgCirc.filters = [shadow];
  sprCont.addChild(bgCirc);

  let iconCol = getColForMat(item.id);
  
  let innerDotSpr  = new PIXI.Graphics();
  innerDotSpr.beginFill(iconCol);
  innerDotSpr.drawCircle(0, 0, 6);
  innerDotSpr.endFill();
  innerDotSpr.filters = [shadow];
  sprCont.addChild(innerDotSpr);

  let matName = item.name || item.id;
  let lableStyleToUse = JSON.parse(JSON.stringify(materialNodeLabelStyle));
  lableStyleToUse.fill = iconCol;
  let matNameNodeLblTxt = new PIXI.Text(matName, lableStyleToUse);
  matNameNodeLblTxt.scale.x = textScaleFactor;
  matNameNodeLblTxt.scale.y = textScaleFactor;
  matNameNodeLblTxt.anchor.set(0.5, -0.8);
  sprCont.addChild(matNameNodeLblTxt);

  sprCont.interactive = true;
  sprCont.buttonMode = true;
  sprCont.hitArea = new PIXI.Circle(0, 0, 40);

  sprCont.scale.set(1.2);
  gsap.to(sprCont, { pixi: { scale: 1 }, duration: 0.3, ease: "power1.in" });
  gsap.to(matNameNodeLblTxt, { pixi: { alpha: 0 }, duration: 1.5, ease: "power1.in" });

  function onPointerIn(evt) {
    gsap.to(innerDotSpr, { pixi: { tint: 0x999999 }, duration: 0.2 }); // no ease
    gsap.to(innerDotSpr, {pixi: { scale: 0.8 }, duration: 0.3, ease: "power1.in" });
    gsap.to(matNameNodeLblTxt, { pixi: { positionY: -4 }, duration: 0.1 }); // no ease
    gsap.to(matNameNodeLblTxt, { pixi: { alpha: 1 }, duration: 0.1, ease: "power1.in" });
  }

  function onPointerOut(evt) {
    gsap.to(innerDotSpr, { pixi: { tint: 0xFFFFFF }, duration: 0.2 }); // no ease
    gsap.to(innerDotSpr, {pixi: { scale: 1 }, duration: 0.3, ease: "power1.in" });
    gsap.to(matNameNodeLblTxt, { pixi: { positionY: 0 }, duration: 0.1 }); // no ease
    gsap.to(matNameNodeLblTxt, { pixi: { alpha: 0 }, duration: 0.1, ease: "power1.in" });
  }

  function onPointerDown(evt) {
    innerDotSpr.tint = 0x333333;
    gsap.to(innerDotSpr, {pixi: { scale: 1.1 }, duration: 0.05, ease: "power1.in" });
  }

  function onPointerUp(evt) {
    innerDotSpr.tint = 0x999999;
    gsap.to(innerDotSpr, {pixi: { scale: 0.8 }, duration: 0.2, ease: "power1.in" });
  }

  sprCont.on("pointerover", onPointerIn);
  sprCont.on("pointerout", onPointerOut);
  sprCont.on("pointerdown", onPointerDown);
  sprCont.on("pointerup", onPointerUp);

  return sprCont;
}

function getGfx_unknown(item) {
  let sprCont = new PIXI.Container();
  
  let shadow = new DropShadowFilter();
  shadow.blur = 8;
  shadow.alpha = 0.2;
  shadow.distance = 2;

  let bgCirc  = new PIXI.Graphics();
  bgCirc.beginFill(0xffffff);
  bgCirc.drawCircle(0, 0, 8);
  bgCirc.endFill();
  bgCirc.filters = [shadow];
  sprCont.addChild(bgCirc);

  let iconCol = "0x676879";
  // iconCol = "0xff0000";
  
  let innerDotSpr  = new PIXI.Graphics();
  innerDotSpr.beginFill(iconCol);
  innerDotSpr.drawCircle(0, 0, 6);
  innerDotSpr.endFill();
  innerDotSpr.filters = [shadow];
  sprCont.addChild(innerDotSpr);

  let matName = item.name || item.id;
  let lableStyleToUse = JSON.parse(JSON.stringify(materialNodeLabelStyle));
  lableStyleToUse.fill = iconCol;
  let matNameNodeLblTxt = new PIXI.Text(matName, lableStyleToUse);
  matNameNodeLblTxt.anchor.set(0.5, -0.8);
  sprCont.addChild(matNameNodeLblTxt);

  sprCont.interactive = true;
  sprCont.buttonMode = true;
  sprCont.hitArea = new PIXI.Circle(0, 0, 40);

  sprCont.scale.set(1.2);
  gsap.to(sprCont, { pixi: { scale: 1 }, duration: 0.3, ease: "power1.in" });
  gsap.to(matNameNodeLblTxt, { pixi: { alpha: 0 }, duration: 1.5, ease: "power1.in" });

  function onPointerIn(evt) {
    gsap.to(innerDotSpr, { pixi: { tint: 0x999999 }, duration: 0.2 }); // no ease
    gsap.to(innerDotSpr, {pixi: { scale: 0.8 }, duration: 0.3, ease: "power1.in" });
    gsap.to(matNameNodeLblTxt, { pixi: { positionY: -4 }, duration: 0.1 }); // no ease
    gsap.to(matNameNodeLblTxt, { pixi: { alpha: 1 }, duration: 0.1, ease: "power1.in" });
  }

  function onPointerOut(evt) {
    gsap.to(innerDotSpr, { pixi: { tint: 0xFFFFFF }, duration: 0.2 }); // no ease
    gsap.to(innerDotSpr, {pixi: { scale: 1 }, duration: 0.3, ease: "power1.in" });
    gsap.to(matNameNodeLblTxt, { pixi: { positionY: 0 }, duration: 0.1 }); // no ease
    gsap.to(matNameNodeLblTxt, { pixi: { alpha: 0 }, duration: 0.1, ease: "power1.in" });
  }

  function onPointerDown(evt) {
    innerDotSpr.tint = 0x333333;
    gsap.to(innerDotSpr, {pixi: { scale: 1.1 }, duration: 0.05 }); // no ease
  }

  function onPointerUp(evt) {
    innerDotSpr.tint = 0x999999;
    gsap.to(innerDotSpr, {pixi: { scale: 0.8 }, duration: 0.2 }); // no ease
  }

  sprCont.on("pointerover", onPointerIn);
  sprCont.on("pointerout", onPointerOut);
  sprCont.on("pointerdown", onPointerDown);
  sprCont.on("pointerup", onPointerUp);

  return sprCont;
}

export function getItemNodeGraphic(item) {
  let sprCont;
  switch (item.ndType) {
    case "board_item": {
      sprCont = getGfx_board_item(item);
      break;
    }
    case "material_node": {
      sprCont = getGfx_material_node(item);
      break;
    }
    case "unknown": 
    default: {
      sprCont = getGfx_unknown(item);
      break;
    }
  }
  return sprCont;
}

export function getItemLinkGraphic() {
  let sprCont = new PIXI.Container();
  let lnBase = new PIXI.Graphics();
  
  sprCont.myGfx = {
    updateLkFfx: (lnSrcPos, lnTgtPos) => {
      lnBase.clear();
      lnBase.lineStyle(4, 0x676879, 0.1);
      lnBase.moveTo(lnSrcPos.x, lnSrcPos.y);
      lnBase.lineTo(lnTgtPos.x, lnTgtPos.y);
    }
  };

  lnBase.alpha = 0;
  gsap.to(lnBase, { pixi: { alpha: 1 }, duration: 0.3, delay: utils.randomNumber(0.3, 0.7) }); // no ease

  sprCont.addChild(lnBase);
  return sprCont;
}







function pol2rect(r, θ) { return { x: r * Math.cos(θ), y: r * Math.sin(θ)}; }


let defaultPixiData = {
  "position": {
    "x": 200,
    "y": 200
  }
};


function createNewPosCandidiate(newNodeInfo, canBeAGoodBaseNode, randomnessSeed = "") {
  let semiRandomIdx = utils.getHash(`${newNodeInfo.id}__${randomnessSeed}`, canBeAGoodBaseNode.length);
  let selectedBaseNode = canBeAGoodBaseNode[semiRandomIdx].node;
  let basePt = selectedBaseNode.pixiData.position;
  let polarDivs = 6;
  let distMin = 80;
  let distRange = 400;
  let angle = (utils.getHash(`${newNodeInfo.id}_${randomnessSeed}`, polarDivs) / polarDivs) * Math.PI * 2;
  let distance = utils.getHash(`${newNodeInfo.id}_${randomnessSeed}`, distRange) + distMin;
  let delta = pol2rect(distance, angle);
  let newPt = {
    x: basePt.x + delta.x,
    y: basePt.y + delta.y,
  };
  return newPt;
}

function computeDistance(n1, n2) {
  return Math.sqrt((Math.pow(n2.x-n1.x,2))+(Math.pow(n2.y-n1.y,2)));
}


function checkIntersections(posCandidate, othNodes) {
  let intersectionThrehold = 60;
  for(let node of othNodes) {
    let d = computeDistance(posCandidate, node.pixiData.position);
    if (d < intersectionThrehold) return true;
  }
  return false;
}


function generateMatNodePos(...args) {
  try {
    let newNodeInfo = args[0];
    let allNodes = args[1] || [];
    let othNodes = allNodes
                    .filter(n => n.id !== newNodeInfo.id)
                    .filter(n => (n.pixiData && n.pixiData.position) );
    let othNodesPts = othNodes.map(n => { return {
      canByMySrc: !!n.converts_to ? n.converts_to.includes(newNodeInfo.id) : false,
      node: n
    }});
    let canBeAGoodBaseNode = othNodesPts.filter(n => n.canByMySrc);
    let attempt = 1;
    let maxAttempts = 30;
    let posCandidate;
    let foundAGoodPos = false;
    while (!foundAGoodPos && attempt <= maxAttempts) {
      posCandidate = createNewPosCandidiate(newNodeInfo, canBeAGoodBaseNode, attempt);
      let doesIntersect = checkIntersections(posCandidate, othNodes);
      if (!doesIntersect) {
        foundAGoodPos = true;
      }
      attempt++;
    };
    let selectedCandidate = posCandidate;
    let pixiData = JSON.parse(JSON.stringify(defaultPixiData));
    pixiData.position.x = selectedCandidate.x;
    pixiData.position.y = selectedCandidate.y;
    return pixiData;
  } catch(err) {
    console.error(err);
    return generateRandomNewPixiData.apply(null, arguments);
  }
}

function generateItemNodePos(...args) {
  try {
    let newNodeInfo = args[0];
    let allNodes = args[1] || [];
    let attempt = 1;
    let maxAttempts = 30;
    let polarDivs = 12;
    let distMin = Math.floor(window.innerWidth / 6);
    let distRange = Math.floor(window.innerWidth / 3);
    let posCandidate;
    let foundAGoodPos = false;
    while (!foundAGoodPos && attempt <= maxAttempts) {
      let angle = (utils.getHash(`${newNodeInfo.id}_${attempt}`, polarDivs) / polarDivs) * Math.PI * 2;
      let distance = utils.getHash(`${newNodeInfo.id}_${attempt}`, distRange) + distMin;
      let delta = pol2rect(distance, angle);
      posCandidate = {
        x: (window.innerWidth / 2) + delta.x,
        y: (window.innerHeight / 2) + delta.y,
      };
      let doesIntersect = checkIntersections(posCandidate, allNodes);
      if (!doesIntersect) {
        foundAGoodPos = true;
      }
      attempt++;
    }

  } catch(err) {
    console.error(err);
    return generateRandomNewPixiData.apply(null, arguments);
  }
}

function generateRandomNewPixiData(...args) {
  let pixiData = JSON.parse(JSON.stringify(defaultPixiData));
  pixiData.position.x = (window.innerWidth / 2) + (utils.randomNumber(-400, 400));
  pixiData.position.y = (window.innerHeight / 2) + (utils.randomNumber(-450, 450));
  return pixiData;
}

let nodPosAlgosDict = {
  "board_item": generateItemNodePos,
  "material_node": generateMatNodePos,
};


export function generatePixiData_ItemNode() {
  let grnShObj_ItemNode = arguments[0];
  let nodePosAlgo = nodPosAlgosDict[grnShObj_ItemNode.ndType];
  if (!nodePosAlgo) nodePosAlgo = generateRandomNewPixiData;
  let retVal = nodePosAlgo.apply(null, arguments);
  return retVal;
}






export function extractLkEdNds(lkId) {
  let splitLkId = lkId.split("__");
  let retObj = {
    ndIdFm: splitLkId[0],
    ndIdTo: splitLkId[1],
  };
  return retObj;
}